import { useCallback, useEffect, useState, useMemo } from "react";
import Log from "../../services/log";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Toolbar from "../controls/Toolbar";
import Utils from "../../utils/utils";

const DomainLogsDetails = () => {
  const [translate] = useTranslation();
  const { id } = useParams();
  const [logs, setLogs] = useState([]);
  const [cursors, setCursors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [logParams, setLogParams] = useState({});

  const getLogs = useCallback(async (params) => {
    const response = await Log.getLogs(params.type, params.firstCursor, params.lastCursor, params.pageIncrement < 0);
    if (!response.error) {
      setLogs(response.data);
      setCurrentPage(page => {
        setHasNextPage(response.paging.count > (page + params.pageIncrement) * response.paging.pageSize)
        return page + params.pageIncrement;
      })
      setCursors({
        firstCursor: response.paging.firstCursor,
        lastCursor: response.paging.lastCursor
      });
    }
  }, [setLogs]);

  useEffect(() => {
    setLogParams({
      pageIncrement: 0,
      type: id
    });
    setHasNextPage(false);
    setCurrentPage(1);
  }, [id]);

  useEffect(() => {
    if (logParams.type) {
      getLogs(logParams);
    }
  }, [getLogs, logParams]);

  const renderLogs = useMemo(() => {
    if (logs.length > 0) {
      return logs.map((log, index) => (
        <li className="list-group-item small" key={index}>
          <div className="row align-items-center mt-n2">
            <div className="col-12 col-lg-6 mt-2">
              <div className="badge bg-secondary p-2">
                {Utils.getFormattedDateTime(log.createdAt)}
              </div>
            </div>
            <div className="col-12 col-lg-6 mt-2 d-lg-flex justify-content-end">
              <div className="badge bg-secondary p-2">
                {log.params?.api || log.error?.api}
              </div>
            </div>
            {(log.params?.user || log.error?.user) &&
              <div className="col-12 mt-2">
                <label className="d-block form-label h6">{translate("OTM_USER")}</label>
                <label className="d-block">{log.params?.user || log.error?.user}</label>
              </div>}
            {log.error?.errorCode &&
              <div className="col-12 col-lg-4 mt-2">
                <label className="d-block form-label h6">{translate("OTM_ERROR_CODE")}</label>
                <label className="d-block">{log.error.errorCode}</label>
              </div>}
            {log.error?.message &&
              <div className="col-12 col-lg-8 mt-2">
                <label className="d-block form-label h6">{translate("OTM_ERROR")}</label>
                <label className="d-block">{log.error.message}</label>
              </div>}
            {log.error?.description &&
              <div className="col-12 mt-2">
                <label className="d-block form-label h6">{translate("OTM_DESCRIPTION")}</label>
                <label className="d-block">{log.error.description}</label>
              </div>}
            {log.params &&
              <div className="col-12 mt-2">
                <label className="d-block form-label h6">{translate("OTM_PARAMS")}</label>
                <textarea className="bg-light w-100" disabled={true} rows="5" value={JSON.stringify(log.params || log.error, null, 2)} />
              </div>
            }
          </div>
        </li>
      ))
    } else {
      return (
        <li className="list-group-item small" key={0}>
          {translate("OTM_NO_LOGS")}
        </li>
      )
    }
  }, [logs, translate]);

  const next = useCallback(() => {
    setLogParams((logParams) => {
      return {
        ...logParams,
        pageIncrement: 1,
        firstCursor: cursors.firstCursor,
        lastCursor: cursors.lastCursor
      }
    });
  }, [cursors.firstCursor, cursors.lastCursor]);

  const previous = useCallback(() => {
    setLogParams((logParams) => {
      return {
        ...logParams,
        pageIncrement: -1,
        firstCursor: cursors.firstCursor,
        lastCursor: cursors.lastCursor
      }
    });
  }, [cursors.firstCursor, cursors.lastCursor]);

  const renderToolbar = useMemo(() => {
    return <Toolbar
      buttons={[
        {
          name: "ChevronLeft",
          title: translate("OTM_PREVIOUS"),
          onClick: previous,
          disabled: currentPage === 1
        },
        {
          name: "ChevronRight",
          title: translate("OTM_NEXT"),
          onClick: next,
          disabled: !hasNextPage
        }
      ]}
      heading={translate("OTM_DOMAIN_SESSION_LOGS")} />
  }, [translate, currentPage, hasNextPage, next, previous]);

  return (
    <div className="card mt-3" style={{ overflowX: 'hidden', overflowY: 'auto' }}>
      <div className="card-header border-bottom">
        {renderToolbar}
      </div>
      <div className="card-body p-0">
        <ul className="list-group list-group-flush">
          {renderLogs}
        </ul>
      </div>
    </div>
  );
};

export default DomainLogsDetails;